// src/pages/Checkout.jsx — v34
// CHANGED FROM v33 (19 Jul 2026): removed the clickable cashback-
// preview block (wallet balance + next-tier progress + "Learn more"
// link to /cashback-program) from the Order Summary card entirely —
// Amit's explicit call, parked for a possible future comeback in a
// different form. The plain "Cashback Program" text link inside the
// "Please read before you pay" checklist below is UNCHANGED and stays
// — that one wasn't flagged.
// CHANGED FROM v32 (19 Jul 2026): /loyalty renamed to /cashback-program
// sitewide — both links on this page (wallet-summary card, terms
// checklist) updated to match.
// CHANGED FROM v31 (18 Jul 2026): GST invoice checkbox feature fully
// reverted per Amit's call — parked for a future session, wasn't worth
// the complexity found (GST rates vary by category and price
// threshold, not a single flat number). Back to v30 behavior exactly.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useStore } from '../context/StoreContext';
import { startRazorpayPayment } from '../utils/payment';
// CHANGED FROM v25: the "Color or size missing" box now lists each
// incomplete cart line individually, naming exactly what's missing
// (color / size / both) with a per-item "Fix now" button, instead of
// one generic message. NOTE: this does not yet fix the underlying
// navigation issue (tapping through still opens a fresh product page
// rather than resuming the cart line in place) — that needs
// StoreContext.jsx to solve properly.
const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh','Puducherry'];
export default function Checkout() {
  const { cart, cartTotal, clearCart, user, authLoading, profileLoading, savedAddresses, saveAddress, defaultAddressId } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState('address');
  const [payMethod, setPayMethod] = useState('upi');
  const [loading, setLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [saveThisAddress, setSaveThisAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState('');
  const [termsAck, setTermsAck] = useState(false);
  const [useWallet, setUseWallet] = useState(false);
  const [walletAck, setWalletAck] = useState(false);
  const [useExpiringRedeem, setUseExpiringRedeem] = useState(false);
  const [showExpiringPopup, setShowExpiringPopup] = useState(false);
  const autoSelected = useRef(false);
  const [form, setForm] = useState({
    name: '', phone: '', flat: '', street: '', landmark: '',
    city: '', state: 'Tamil Nadu', pin: ''
  });
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
  }, [user, authLoading, navigate]);
  useEffect(() => {
    if (!autoSelected.current && user && defaultAddressId && savedAddresses.length > 0) {
      const def = savedAddresses.find(a => a.id === defaultAddressId);
      if (def) {
        setSelectedAddressId(def.id);
        setForm(def);
        autoSelected.current = true;
      }
    }
  }, [user, defaultAddressId, savedAddresses]);
  const hasShipExtra = cart.some(i => !i.shipping?.toUpperCase().includes('FREE'));
  function needsSizeFor(item) {
    return !!item.hasSizing && (item.category === 'Garments' || item.category === 'Shoes');
  }
  function cartItemMissingSelection(item) {
    const needsColor = (item.images?.length || 0) > 1;
    const missingColor = needsColor && !item.colorCode;
    const missingSize = needsSizeFor(item) && !item.size;
    return missingColor || missingSize;
  }
  function describeMissing(item) {
    const needsColor = (item.images?.length || 0) > 1;
    const missingColor = needsColor && !item.colorCode;
    const missingSize = needsSizeFor(item) && !item.size;
    if (missingColor && missingSize) return 'color and size';
    if (missingColor) return 'color';
    if (missingSize) return 'size';
    return '';
  }
  const incompleteItems = cart.filter(cartItemMissingSelection);
  const hasIncompleteSelection = incompleteItems.length > 0;
  const waitingOnProfile = !!user && profileLoading;
  const wallet = user?.wallet;
  const canRedeem = !!(wallet && wallet.isRedeemable);
  const usableAmount = canRedeem ? Math.floor(Math.min(wallet.walletBalance, cartTotal * 0.5)) : 0;
  const expiringAmount = wallet?.expiringSoonAmount || 0;
  const hasExpiringPoints = expiringAmount > 0;
  const walletUsedFinal = useExpiringRedeem ? expiringAmount : (useWallet ? usableAmount : 0);
  const amountPayable = Math.max(0, cartTotal - walletUsedFinal);
  const earnRate = wallet ? wallet.earnRate : 0.01;
  const tierLabel = wallet ? wallet.tier : 'Just Arrived';
  const cashbackPreview = Math.round(amountPayable * earnRate * 100) / 100;
  function isAddressComplete() {
    return !!(form.name && form.phone && form.flat && form.landmark && form.city && form.pin);
  }
  function updateForm(key, val) {
    setForm(f => ({ ...f, [key]: val }));
  }
  function handleAddressSelect(id) {
    setSelectedAddressId(id);
    if (id !== 'new') {
      const addr = savedAddresses.find(a => a.id === id);
      if (addr) setForm(addr);
    } else {
      setForm({ name: '', phone: '', flat: '', street: '', landmark: '', city: '', state: 'Tamil Nadu', pin: '' });
    }
  }
  function handleUseThisAddress() {
    if (!isAddressComplete()) {
      alert('Please fill in all required fields, including landmark');
      return;
    }
    if (saveThisAddress) {
      saveAddress({ ...form, label: addressLabel || 'Address' });
    }
    setStep('payment');
  }
  async function handlePay() {
    if (hasIncompleteSelection) {
      alert('One or more items are missing a color or size selection. Please update them in your cart before continuing.');
      return;
    }
    if (waitingOnProfile) {
      alert('Still loading your account details — please wait a moment and try again.');
      return;
    }
    if (!termsAck) {
      alert('Please confirm you have read and understood the order terms before paying');
      return;
    }
    if (useWallet && !walletAck) {
      alert('Please acknowledge the nb wallet terms before paying');
      return;
    }
    setLoading(true);
    const orderId = 'NBS-' + Date.now();
    sessionStorage.setItem('nbs_order', JSON.stringify({ orderId, total: amountPayable, cart, address: form, walletUsed: walletUsedFinal }));
    const itemsStr = cart.map(i => `${i.id}${i.size ? ' (' + i.size + ')' : ''} x${i.qty} @₹${i.price}`).join(' | ');
    const itemDetails = cart.map(i => ({
      productId: i.id,
      colorCode: i.colorCode || '',
      colorImageUrl: i.colorImageUrl || '',
      size: i.size || 'NA',
      lineKey: i.key,
    }));
    const addressStr = `${form.flat}, ${form.street ? form.street + ', ' : ''}${form.landmark}, ${form.city}, ${form.state} - ${form.pin}`;
    const shippingTypeStr = hasShipExtra ? 'Ship Extra' : 'Free';
    startRazorpayPayment({
      amount: amountPayable,
      orderId,
      customerId: user?.customerId || '',
      customerUid: user?.uid || '',
      customerName: form.name,
      customerPhone: form.phone,
      items: itemsStr,
      itemDetails,
      address: addressStr,
      shippingType: shippingTypeStr,
      walletUsed: walletUsedFinal,
      onSuccess: (razorpayResponse) => {
        sessionStorage.setItem('nbs_order_payment', JSON.stringify(razorpayResponse));
        clearCart();
        navigate('/order-success');
      },
      onFailure: (message) => {
        alert(message);
        setLoading(false);
      },
    });
  }
  const selectedAddr = selectedAddressId !== 'new' ? savedAddresses.find(a => a.id === selectedAddressId) : null;
  if (authLoading || !user) return null;
  return (
    <div className="app-shell">
      <Navbar
        showBack
        title="Checkout"
        onBack={step === 'payment' ? () => setStep('address') : undefined}
      />
      <div className="checkout-steps">
        <div className="step done"><div className="step-dot"><i className="ti ti-check" style={{ fontSize: 11 }}></i></div><span>Cart</span></div>
        <div className="step-line"></div>
        <div className={`step ${step === 'address' ? 'active' : 'done'}`}>
          <div className="step-dot">{step === 'address' ? '2' : <i className="ti ti-check" style={{ fontSize: 11 }}></i>}</div>
          <span>Delivery</span>
        </div>
        <div className="step-line"></div>
        <div className={`step ${step === 'payment' ? 'active' : ''}`}><div className="step-dot">3</div><span>Payment</span></div>
      </div>
      {step === 'address' && (
        <div className="page-body" style={{ paddingBottom: 100 }}>
          {savedAddresses.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="section-title" style={{ marginBottom: 10 }}>Saved addresses</div>
              <div className="addr-chip-row">
                {savedAddresses.map(a => (
                  <div
                    key={a.id}
                    className={`addr-chip ${selectedAddressId === a.id ? 'selected' : ''}`}
                    onClick={() => handleAddressSelect(a.id)}
                  >
                    {a.label || a.name}
                    {selectedAddressId === a.id && <i className="ti ti-check" style={{ fontSize: 12, marginLeft: 4 }}></i>}
                  </div>
                ))}
                <div
                  className={`addr-chip addr-chip-new ${selectedAddressId === 'new' ? 'selected' : ''}`}
                  onClick={() => handleAddressSelect('new')}
                >
                  + New
                </div>
              </div>
              {selectedAddr && (
                <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)', marginBottom: 2 }}>{selectedAddr.name}</div>
                  <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                    {selectedAddr.flat}{selectedAddr.street ? `, ${selectedAddr.street}` : ''}<br />
                    {selectedAddr.landmark && <>Near {selectedAddr.landmark}<br /></>}
                    {selectedAddr.city}, {selectedAddr.state} - {selectedAddr.pin}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{selectedAddr.phone}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Order & WhatsApp updates go to this number</div>
                </div>
              )}
            </div>
          )}
          {selectedAddressId === 'new' && (
            <div className="card">
              <div className="section-title" style={{ marginBottom: 14 }}>Delivery address</div>
              {[
                ['name', 'Full name *', 'text', 'Recipient name'],
                ['phone', 'Mobile number * (used for order & WhatsApp updates)', 'tel', '+91 00000 00000'],
                ['flat', 'Flat / House / Block no. *', 'text', 'e.g. 4B, Second Floor'],
                ['street', 'Street / Area', 'text', 'Street name and area'],
                ['landmark', 'Landmark *', 'text', 'e.g. Near Apollo Hospital'],
              ].map(([key, label, type, ph]) => (
                <div className="form-field" key={key}>
                  <label>{label}</label>
                  <input type={type} placeholder={ph} value={form[key]} onChange={e => updateForm(key, e.target.value)} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-field">
                  <label>City *</label>
                  <input type="text" placeholder="City" value={form.city} onChange={e => updateForm('city', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>PIN code *</label>
                  <input type="text" placeholder="600001" maxLength={6} value={form.pin} onChange={e => updateForm('pin', e.target.value)} />
                </div>
              </div>
              <div className="form-field">
                <label>State</label>
                <select value={form.state} onChange={e => updateForm('state', e.target.value)}>
                  {STATES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--navy)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={saveThisAddress}
                    onChange={e => setSaveThisAddress(e.target.checked)}
                  />
                  Save this address to my account
                </label>
                {saveThisAddress && (
                  <input
                    type="text"
                    placeholder="Label this address (e.g. Home, Office, Ravi - Chennai)"
                    value={addressLabel}
                    onChange={e => setAddressLabel(e.target.value)}
                    style={{ width: '100%', marginTop: 8, padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', fontFamily: 'var(--font)', outline: 'none' }}
                  />
                )}
              </div>
            </div>
          )}
          <button className="btn-blue" style={{ width: '100%' }} onClick={handleUseThisAddress}>
            Use this address for delivery
          </button>
        </div>
      )}
      {step === 'payment' && (
        <div className="page-body" style={{ paddingBottom: 160 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="section-title">Delivering to</div>
              <button
                onClick={() => setStep('address')}
                style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}
              >
                Change
              </button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 500, marginBottom: 2 }}>{form.name}</div>
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
              {form.flat}, {form.street ? form.street + ', ' : ''}Near {form.landmark}<br />
              {form.city}, {form.state} - {form.pin}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{form.phone}</div>
          </div>
          {hasIncompleteSelection && (
            <div style={{ background: '#FDECEC', border: '1.5px solid #D64545', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <i className="ti ti-alert-circle" style={{ fontSize: 26, color: '#D64545', marginBottom: 8, display: 'block' }}></i>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#D64545' }}>
                  {incompleteItems.length === 1 ? '1 item needs' : `${incompleteItems.length} items need`} your attention
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {incompleteItems.map(item => (
                  <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderRadius: 6, padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, color: '#7A2E2E' }}>
                      <strong>{item.name}</strong> — {describeMissing(item)} missing
                    </div>
                    <button
                      onClick={() => navigate(`/product/${item.id}`, {
                        state: { editKey: item.key, editSize: item.size || '', editColorCode: item.colorCode || '', editQty: item.qty },
                      })}
                      style={{ background: '#D64545', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)', flexShrink: 0, marginLeft: 10 }}
                    >
                      Fix now
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#7A2E2E', textAlign: 'center', lineHeight: 1.6 }}>
                Tapping "Fix now" opens the product page pre-filled with what you already picked — just complete the missing option and tap "Update cart item".
              </div>
            </div>
          )}
          {hasShipExtra && (
            <div style={{ background: '#FEF8E8', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, color: '#854F0B', lineHeight: 1.6 }}>
              <i className="ti ti-info-circle" style={{ marginRight: 6 }}></i>
              One or more items have shipping charges. Exact amount will be confirmed via WhatsApp after order.
            </div>
          )}
          <div className="card">
            <div className="section-title" style={{ marginBottom: 14 }}>Payment method</div>
            <div className="pay-opts">
              {[
                ['upi', 'ti-qrcode', 'UPI / QR code', 'PhonePe, GPay, Paytm & more'],
                ['card', 'ti-credit-card', 'Card payment', 'Debit or credit card'],
                ['netbanking', 'ti-building-bank', 'Net banking', 'All major banks'],
              ].map(([id, icon, label, sub]) => (
                <div key={id} className={`pay-opt ${payMethod === id ? 'selected' : ''}`} onClick={() => setPayMethod(id)}>
                  <div className={`pay-radio ${payMethod === id ? 'on' : ''}`}></div>
                  <i className={`ti ${icon}`} style={{ fontSize: 22 }}></i>
                  <div><div className="pay-label">{label}</div><div className="pay-sub">{sub}</div></div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8 }}>
              All methods are handled securely by Razorpay's checkout — you'll pick your exact method there.
            </div>
          </div>
          {wallet && (
            <div className="card">
              <div className="section-title" style={{ marginBottom: 10 }}>nb wallet</div>
              {hasExpiringPoints && (
                <div style={{ background: '#FEF8E8', border: '1px solid var(--gold)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <i className="ti ti-alert-triangle" style={{ color: 'var(--gold)', fontSize: 16 }}></i>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#854F0B' }}>₹{expiringAmount.toLocaleString()} expiring within 30 days</span>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--navy)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useExpiringRedeem}
                      onChange={e => {
                        if (e.target.checked) {
                          setShowExpiringPopup(true);
                        } else {
                          setUseExpiringRedeem(false);
                        }
                      }}
                    />
                    Redeem my expiring points now
                  </label>
                  {useExpiringRedeem && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#854F0B', marginTop: 8, fontWeight: 600 }}>
                      <span>Expiring points applied</span>
                      <span>−₹{expiringAmount.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
              {canRedeem && !useExpiringRedeem ? (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--navy)', cursor: 'pointer', marginBottom: useWallet ? 10 : 0 }}>
                    <input
                      type="checkbox"
                      checked={useWallet}
                      onChange={e => { setUseWallet(e.target.checked); if (!e.target.checked) setWalletAck(false); }}
                    />
                    Use my nb wallet for this order (₹{usableAmount.toLocaleString()} available — max 50% of order value)
                  </label>
                  {useWallet && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--blue)', marginBottom: 10, padding: '6px 10px', background: '#EEF4FF', borderRadius: 6 }}>
                        <span>nb wallet applied</span>
                        <span>−₹{walletUsedFinal.toLocaleString()}</span>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: 'var(--muted)', cursor: 'pointer', lineHeight: 1.5 }}>
                        <input
                          type="checkbox"
                          checked={walletAck}
                          onChange={e => setWalletAck(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        I understand nb wallet can only be used when balance exceeds ₹25, up to 50% of this order's value, and once used, nb wallet credit cannot be transferred back to my bank account.
                      </label>
                    </>
                  )}
                </>
              ) : !canRedeem && !hasExpiringPoints ? (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Your nb wallet balance (₹{wallet.walletBalance.toLocaleString()}) unlocks for use once it crosses ₹25.
                </div>
              ) : null}
            </div>
          )}
          {showExpiringPopup && (
            <div className="expiring-popup-overlay" onClick={() => setShowExpiringPopup(false)}>
              <div className="expiring-popup" onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', marginBottom: 12 }}>
                  Redeem ₹{expiringAmount.toLocaleString()} expiring points?
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#444', lineHeight: 1.9, marginBottom: 16 }}>
                  <li>Exactly <strong>₹{expiringAmount.toLocaleString()}</strong> will be applied to this order — no more, no less.</li>
                  <li>This does <strong>not</strong> count toward the usual 50%-of-order redemption cap.</li>
                  <li>You cannot also apply your regular nb wallet balance to this same order — it's one or the other.</li>
                  <li>The oldest points in your wallet are used first, so this clears what was about to expire.</li>
                </ul>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setShowExpiringPopup(false)}
                    style={{ flex: 1, background: 'transparent', border: '0.5px solid var(--border)', color: 'var(--muted)', borderRadius: 6, padding: 12, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { setUseExpiringRedeem(true); setUseWallet(false); setWalletAck(false); setShowExpiringPopup(false); }}
                    style={{ flex: 1.5, background: 'var(--gold)', border: 'none', color: '#fff', borderRadius: 6, padding: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                  >
                    Confirm & apply
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="card">
            <div className="section-title" style={{ marginBottom: 12 }}>Order summary</div>
            {cart.map(item => {
              const isFree = item.shipping?.toUpperCase().includes('FREE');
              return (
                <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#555', marginBottom: 8 }}>
                  <span>{item.name}{item.colorCode ? ` · ${item.colorCode}` : ''}{item.size ? ` (${item.size})` : ''} × {item.qty}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    ₹{(item.price * item.qty).toLocaleString()}
                    <span style={{
                      fontSize: 9,
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: 4,
                      color: '#fff',
                      background: isFree ? 'var(--green)' : '#D64545',
                      whiteSpace: 'nowrap',
                    }}>
                      {isFree ? 'Free Ship' : 'Ship Extra'}
                    </span>
                  </span>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: hasShipExtra ? '#D4A020' : 'var(--green)', marginBottom: 8 }}>
              <span>Shipping</span><span>{hasShipExtra ? 'To be confirmed' : 'Free'}</span>
            </div>
            {walletUsedFinal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--blue)', marginBottom: 8 }}>
                <span>nb wallet applied</span><span>−₹{walletUsedFinal.toLocaleString()}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 500, borderTop: '0.5px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
              <span>Total payable</span>
              <span>₹{amountPayable.toLocaleString()}{hasShipExtra ? ' + ship' : ''}</span>
            </div>
          </div>
          <div style={{ background: '#FDECEC', border: '1.5px solid #D64545', borderRadius: 8, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#D64545', marginBottom: 8 }}>
              Please read before you pay
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#7A2E2E', lineHeight: 1.9 }}>
              <li>Orders cannot be cancelled once payment is completed.</li>
              <li>Shipping charges are non-refundable. If an eligible return is approved, we arrange pickup at our own cost — you won't pay for return shipping. See our <Link to="/shipping" style={{ color: '#7A2E2E', fontWeight: 700, textDecoration: 'underline' }}>Shipping Policy</Link>.</li>
              <li>Product colors may appear slightly different from photos due to lighting and screen settings. Please check size charts carefully before ordering. See our <Link to="/returns" style={{ color: '#7A2E2E', fontWeight: 700, textDecoration: 'underline' }}>Return Policy</Link>.</li>
              <li>Your order will be dispatched within 2-3 days. Ship Extra charges (if any) will be confirmed via WhatsApp.</li>
              <li><Link to="/cashback-program" style={{ color: '#7A2E2E', fontWeight: 700, textDecoration: 'underline' }}>Cashback Program</Link> tier progress is based on items you keep — returned items don't count toward your spend slab.</li>
            </ul>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, fontWeight: 600, color: '#D64545', cursor: 'pointer', marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(214,69,69,0.25)' }}>
              <input
                type="checkbox"
                checked={termsAck}
                onChange={e => setTermsAck(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              I have read and understood all of the above
            </label>
          </div>
        </div>
      )}
      {step === 'payment' && (
        <div className="pay-footer">
          {waitingOnProfile && (
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
              Loading your account details…
            </div>
          )}
          <button className="btn-blue" onClick={handlePay} disabled={loading || !termsAck || waitingOnProfile || hasIncompleteSelection}>
            <i className="ti ti-lock"></i>
            {loading ? ' Processing…' : ` Pay ₹${amountPayable.toLocaleString()}`}
          </button>
          <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--muted)', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <i className="ti ti-shield-check" style={{ fontSize: 12 }}></i> 100% secure · powered by Razorpay
          </div>
        </div>
      )}
      <style>{`
        .checkout-steps { display:flex; align-items:center; padding:14px 18px; background:#fff; border-bottom:0.5px solid var(--card-border); }
        .step { display:flex; flex-direction:column; align-items:center; gap:4px; }
        .step-dot { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:500; background:rgba(27,42,74,0.1); color:var(--muted); }
        .step.done .step-dot { background:var(--blue); color:#fff; }
        .step.active .step-dot { background:var(--navy); color:var(--off-white); }
        .step span { font-size:9px; letter-spacing:0.06em; color:var(--muted); text-transform:uppercase; }
        .step.active span { color:var(--navy); }
        .step-line { flex:1; height:0.5px; background:var(--border); margin:0 6px 14px; }
        .form-field { margin-bottom:12px; }
        .form-field label { display:block; font-size:11px; color:var(--muted); margin-bottom:4px; }
        .form-field input, .form-field select { width:100%; padding:10px 12px; border:0.5px solid rgba(27,42,74,0.2); border-radius:6px; font-size:13px; color:var(--navy); background:var(--off-white); font-family:var(--font); outline:none; }
        .form-field input:focus { border-color:var(--blue); background:#fff; }
        .addr-chip-row { display:flex; gap:8px; overflow-x:auto; padding-bottom:4px; margin-bottom:12px; }
        .addr-chip { flex-shrink:0; padding:8px 14px; border-radius:20px; border:0.5px solid rgba(27,42,74,0.25); color:var(--navy); font-size:12px; white-space:nowrap; cursor:pointer; display:flex; align-items:center; }
        .addr-chip.selected { background:var(--blue); border-color:var(--blue); color:#fff; }
        .addr-chip-new { border-style:dashed; color:var(--blue); }
        .addr-chip-new.selected { background:var(--blue); border-style:solid; color:#fff; }
        .pay-opts { display:flex; flex-direction:column; gap:10px; }
        .pay-opt { display:flex; align-items:center; gap:12px; padding:12px 14px; border:0.5px solid rgba(27,42,74,0.15); border-radius:8px; cursor:pointer; background:var(--off-white); }
        .pay-opt.selected { border-color:var(--blue); background:#EEF4FF; }
        .pay-radio { width:16px; height:16px; border-radius:50%; border:1.5px solid rgba(27,42,74,0.3); flex-shrink:0; }
        .pay-radio.on { border-color:var(--blue); background:radial-gradient(circle, var(--blue) 50%, transparent 55%); }
        .pay-label { font-size:13px; font-weight:500; color:var(--navy); }
        .pay-sub { font-size:11px; color:var(--muted); }
        .pay-footer { position:fixed; bottom:0; left:50%; transform:translateX(-50%); width:100%; max-width:430px; background:#fff; border-top:0.5px solid var(--border); padding:12px 18px 20px; z-index:50; }
        .pay-footer .btn-blue { display:flex; align-items:center; justify-content:center; gap:8px; width:100%; }
        .pay-footer .btn-blue:disabled { opacity:0.6; cursor:not-allowed; }
        .expiring-popup-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 250; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .expiring-popup { background: #fff; border-radius: 12px; padding: 20px; width: 100%; max-width: 380px; }
      `}</style>
    </div>
  );
}